import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { CommonModule, DOCUMENT, Location } from '@angular/common';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivityDrawerHostComponent } from '../../components/activity-drawer-host/activity-drawer-host.component';
import { SummaryCardComponent } from '../../components/summary-card/summary-card.component';
import { CancelActivityDialogComponent } from '../../components/cancel-activity-dialog/cancel-activity-dialog.component';
import { CancelPaidActivityDialogComponent } from '../../components/cancel-paid-activity-dialog/cancel-paid-activity-dialog.component';
import { ActivitiesManagementApiService } from '../../../services/activities-management/activities-management-api.service';
import { ActivitiesManagementPageSessionService } from '../../../services/activities-management/activities-management-page-session.service';
import { formatDisplayDate, fromIsoDate, toIsoDate } from '../../utils/management-date.util';
import { ManagementListDebounce } from '../../utils/management-list-debounce.util';
import { toTwoColumnOptions } from '../../utils/management-options-layout.util';
import { getSportIconPath, getSportOptionValue } from '../../utils/management-sport.util';
import { getManagementTableLazyState, getPreviousPageFirst } from '../../utils/management-table-state.util';
import { ManagementToastTimer } from '../../utils/management-toast-timer.util';
import { ManagementViewportScrollService } from '../../../services/shared/management-viewport-scroll.service';
import type {
  ActivitiesListQuery,
  ActivitiesListSortField,
  ActivitiesSummary,
  ActivityStatus,
  EditGroupActivity,
  SportOption,
  SportTypeConfig
} from '../../models/activities-management.model';
import { DEFAULT_GROUP_COVER_URL } from '../../constants/management-ui.constants';
import { growthTone } from '../../../services/growth/growth.store.service';

@Component({
  selector: 'bo-activities-management',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TooltipModule,
    CalendarModule,
    FormsModule,
    TooltipModule,
    ActivityDrawerHostComponent,
    SummaryCardComponent,
    CancelActivityDialogComponent,
    CancelPaidActivityDialogComponent
  ],
  templateUrl: './activities-management.component.html',
  styleUrls: ['./activities-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivitiesManagementComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ActivityDrawerHostComponent, { static: true }) activityDrawerHost!: ActivityDrawerHostComponent;
  @ViewChild('activitiesTable') private activitiesTable?: Table;
  @ViewChild('dateRangeCalendar') dateRangeCalendar?: Calendar;
  @ViewChild('dateRangeField') dateRangeField?: ElementRef<HTMLElement>;

  activities: EditGroupActivity[] = [];
  totalRecords = 0;
  tableFirst = 0;
  tableRows = 10;
  tableLoading = false;
  tableSortField?: ActivitiesListSortField;
  tableSortOrder?: 'asc' | 'desc';
  get tableSortOrderValue(): number {
    return this.tableSortOrder === 'asc' ? 1 : this.tableSortOrder === 'desc' ? -1 : 0;
  }

  sportTypeConfigs: SportTypeConfig[] = [];
  editGroupSportOptions: SportOption[] = [];
  defaultCoverPhotoUrl = DEFAULT_GROUP_COVER_URL;
  operationErrorMessage = '';
  deleteSuccessMessage = '';
  searchQuery = '';
  showFilter = false;
  selectedStatus = 'all';
  /** Exact sport identifier, empty meaning every sport. */
  selectedSportId = '';
  /** Free text matched against the activity address, not a city name. */
  locationQuery = '';

  /** Sport list for the filter, with an entry that clears it. */
  get sportFilterOptions(): { label: string; value: string }[] {
    return [
      { label: this.translate.instant('activitiesManagement.allSports'), value: '' },
      ...this.sportTypeConfigs.map(({ label, value }) => ({ label, value }))
    ];
  }
  selectedStartDate = '';
  selectedEndDate = '';
  dateRangeSelection: Date[] | undefined;
  dateRangeCalendarPanelStyle: Record<string, string> = {};
  statusOptions: { label: string; value: string }[] = [];
  /** One-click table cuts, combinable with the detailed filters below them. */
  quickWithoutPartners = false;
  quickFull = false;
  quickWeekly = false;
  quickThisWeek = false;
  activitySummary: ActivitiesSummary = {
    totalActivities: 0,
    activitiesCreatedThisMonth: 0,
    totalParticipants: 0,
    incoming: 0,
    completionRate: 0,
    completionDelta: 0,
    partnerSeats: 0,
    partnerSeatsGrowthPct: null,
    createdGrowthPct: null,
    upcomingWithoutPartners: 0
  };
  summaryLoading = true;

  cancelDialogVisible = false;
  cancelDialogClosing = false;
  skipCancelDialogEnterAnimation = false;
  cancelConfirmed = false;
  activityToCancel: EditGroupActivity | null = null;

  cancelPaidDialogVisible = false;
  cancelPaidDialogClosing = false;
  skipCancelPaidDialogEnterAnimation = false;
  cancelPaidConfirmed = false;

  private readonly subscriptions = new Subscription();
  private activitiesRequestSubscription: Subscription | null = null;
  private readonly searchReload = new ManagementListDebounce();
  private readonly toastDismiss = new ManagementToastTimer();
  private initialPageScrollRestored = false;
  private scrollRestorationPending = false;
  private pendingPageScrollRestore = 0;
  private hasEarlyPageScrollLock = false;
  private postRestoreScrollGuardFrame: number | null = null;
  private postRestoreScrollGuardUntil = 0;
  private postRestoreScrollGuardLockActive = false;
  private postRestoreTargetScrollY = 0;
  private pageScrollPosition = 0;
  private modalScrollPosition = 0;
  private isReloadNavigation = false;
  private modalViewportLocked = false;
  private restoringModalSession = false;
  private lastActivityDeleteDialogActive = false;
  private initialActivityRouteRestored = false;
  private inFlightActivitiesQueryKey = '';
  private loadedActivitiesQueryKey = '';
  private shouldIgnoreInitialLazyLoad = true;
  private readonly modalScrollKeys = new Set(['ArrowDown', 'ArrowUp', 'End', 'Home', 'PageDown', 'PageUp', ' ']);
  private readonly preventBackgroundPointerScroll = (event: Event) => {
    if (!this.isAnyModalVisible()) {
      return;
    }

    if (this.viewportScroll.isDrawerScrollEvent(event, this.isDeleteDialogBlocked())) {
      return;
    }

    event.preventDefault();
  };
  private readonly preventBackgroundKeyboardScroll = (event: KeyboardEvent) => {
    if (
      this.isAnyModalVisible() &&
      this.modalScrollKeys.has(event.key) &&
      !this.isTopDrawerBodyTarget(event.target)
    ) {
      event.preventDefault();
    }
  };

  constructor(
    private activitiesApi: ActivitiesManagementApiService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private pageSession: ActivitiesManagementPageSessionService,
    private viewportScroll: ManagementViewportScrollService,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.viewportScroll.setManualHistoryScrollRestoration();
    this.isReloadNavigation = this.viewportScroll.isReloadNavigation();
    this.applyStoredPageState();
    this.prepareEarlyPageScrollLock();
  }


  ngOnInit() {
    this.document.documentElement.classList.add('groups-page-scroll-stable');
    this.subscriptions.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && !event.url.startsWith('/activities')) {
        this.pageSession.clearPageState();
        this.pageSession.clearModalState();
      }
    }));
    this.buildStatusOptions();
    this.subscriptions.add(this.translate.onLangChange.subscribe(() => {
      this.buildStatusOptions();
      this.cdr.markForCheck();
    }));
    this.restoreInitialActivityRoute();
    this.loadReferenceData();
    this.loadActivities(true, true);

    this.subscriptions.add(
      this.route.queryParamMap.subscribe(params => {
        const activityId = params.get('activity');
        if (this.shouldClearReloadedStandaloneDelete()) {
          const restoreScrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : this.pageScrollPosition;
          this.pageSession.clearModalState();
          this.clearActivityQuery(restoreScrollY);
          return;
        }

        if (activityId) {
          if (this.isAnyModalVisible() && this.activityDrawerHost?.activityDraft?.id === activityId) {
            return;
          }

          this.subscriptions.add(this.activitiesApi.getDetail(activityId).subscribe({
            next: activity => {
              if (!this.activityDrawerHost) {
                return;
              }
              if (this.isAnyModalVisible() && this.activityDrawerHost.activityDraft?.id === activity.id) {
                return;
              }
              this.restoreActivityFromUrl(activity);
              this.cdr.markForCheck();
            },
            error: error => {
              this.showOperationError(this.getApiErrorMessage(
                error,
                'Unable to load activity detail. Please try again.'
              ));
              this.cdr.markForCheck();
            }
          }));
        } else if (this.activityDrawerHost?.visible) {
          this.activityDrawerHost.close();
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngAfterViewInit() {
    if (this.initialActivityRouteRestored) {
      this.cdr.markForCheck();
      return;
    }

    this.restoreInitialActivityRoute();
  }

  private restoreInitialActivityRoute() {
    const activityId = this.route.snapshot.queryParamMap.get('activity');
    if (!activityId) {
      return;
    }

    if (this.shouldClearReloadedStandaloneDelete()) {
      this.pageSession.clearModalState();
      this.clearActivityQuery(this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : this.pageScrollPosition);
      this.initialActivityRouteRestored = true;
      this.initialPageScrollRestored = true;
      this.releasePendingPageScrollLock();
      return;
    }

    if (this.restoreActivityModalSessionFromStorage(activityId)) {
      this.initialActivityRouteRestored = true;
      this.cdr.markForCheck();
      return;
    }

    this.subscriptions.add(this.activitiesApi.getDetail(activityId).subscribe({
      next: activity => {
        if (!this.restoreActivityModalSession(activity)) {
          this.restoreActivityFromUrl(activity);
          this.initialPageScrollRestored = true;
          this.releasePendingPageScrollLock();
        }
        this.initialActivityRouteRestored = true;
        this.cdr.markForCheck();
      },
      error: error => {
        this.initialActivityRouteRestored = true;
        this.initialPageScrollRestored = true;
        this.releasePendingPageScrollLock();
        this.showOperationError(this.getApiErrorMessage(
          error,
          'Unable to load activity detail. Please try again.'
        ));
        this.cdr.markForCheck();
      }
    }));
  }

  ngOnDestroy() {
    this.stopPostRestoreScrollGuard();
    this.releasePendingPageScrollLock();
    this.unlockModalViewport();
    this.document.documentElement.classList.remove('groups-page-scroll-stable');
    this.document.documentElement.classList.remove('groups-scroll-restoring');
    this.subscriptions.unsubscribe();
    this.activitiesRequestSubscription?.unsubscribe();
    this.searchReload.clear();
    this.clearToastTimeout();
  }

  openActivityFromList(activity: EditGroupActivity, syncUrl = true, options?: { editMode?: boolean; keepUndoInEditMode?: boolean; skipEnterAnimation?: boolean }) {
    const scrollY = this.document.defaultView?.scrollY ?? this.pageScrollPosition;
    this.pageScrollPosition = scrollY;
    this.persistPageState();
    this.activityDrawerHost.markInitialized();
    this.activityDrawerHost.open(activity, options);
    this.updateModalViewportLock(scrollY);
    this.persistModalSession();
    if (syncUrl) {
      this.setActivityQuery(activity.id, scrollY);
    }
  }

  preventRowTextActivation(event: PointerEvent) {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select, [role="button"]')) {
      return;
    }

    event.preventDefault();
  }

  openEditActivityMode(activity: EditGroupActivity) {
    const scrollY = this.document.defaultView?.scrollY ?? this.pageScrollPosition;
    this.pageScrollPosition = scrollY;
    this.persistPageState();
    this.activityDrawerHost.markInitialized();
    this.activityDrawerHost.open(activity, { editMode: true, keepUndoInEditMode: true });
    this.updateModalViewportLock(scrollY);
    this.persistModalSession();
    this.syncActivityModeUrl(activity.id, scrollY, 'edit');
  }

  onActivitySaved(activity: EditGroupActivity) {
    this.activities = this.activities.map(item => item.id === activity.id ? { ...activity } : item);
    this.cdr.markForCheck();
  }

  onActivityDeleted(activity: EditGroupActivity) {
    this.loadActivities(true, true);
    this.loadSummaryData();
    this.updateModalViewportLock();
    this.deleteSuccessMessage = `Activity "${activity.name}" has been moved to Deleted items`;
    this.scheduleToastDismiss(() => {
      this.deleteSuccessMessage = '';
    });
    this.cdr.markForCheck();
  }

  onActivityCancelled(activity: EditGroupActivity) {
    this.loadActivities(true, true);
    this.loadSummaryData();
    this.updateModalViewportLock();
    this.deleteSuccessMessage = `Activity "${activity.name}" has been canceled`;
    this.scheduleToastDismiss(() => {
      this.deleteSuccessMessage = '';
    });
    this.cdr.markForCheck();
  }

  dismissDeleteSuccess() {
    this.clearToastTimeout();
    this.deleteSuccessMessage = '';
    this.cdr.markForCheck();
  }

  onOperationErrorChange(message: string) {
    if (message) {
      this.showOperationError(message);
    } else {
      this.operationErrorMessage = '';
    }
    this.cdr.markForCheck();
  }

  onActivityVisibilityChange(visible: boolean) {
    if (this.restoringModalSession) {
      return;
    }

    const deleteDialogActive = !!this.activityDrawerHost?.deleteDialogVisible || !!this.activityDrawerHost?.deleteDialogClosing;
    if (!visible && !deleteDialogActive) {
      const restoreScrollY = this.modalViewportLocked ? this.modalScrollPosition : this.pageScrollPosition;
      this.pageScrollPosition = restoreScrollY;
      this.updateModalViewportLock(restoreScrollY);
      this.clearActivityQuery(restoreScrollY);
      this.pageSession.clearModalState();
      this.scheduleIdleModalCleanup();
      return;
    }
    this.updateModalViewportLock(this.modalViewportLocked ? this.modalScrollPosition : undefined);
  }

  onActivitySessionChange() {
    if (this.restoringModalSession) {
      return;
    }

    const deleteDialogActive = !!this.activityDrawerHost?.deleteDialogVisible || !!this.activityDrawerHost?.deleteDialogClosing;
    if (deleteDialogActive !== this.lastActivityDeleteDialogActive || (!this.isAnyModalVisible() && this.modalViewportLocked)) {
      const stableScrollY = this.modalViewportLocked
        ? this.modalScrollPosition
        : this.document.defaultView?.scrollY ?? this.pageScrollPosition;
      this.updateModalViewportLock(stableScrollY);
    }
    this.lastActivityDeleteDialogActive = deleteDialogActive;
    if (!this.isAnyModalVisible()) {
      const restoreScrollY = this.modalViewportLocked ? this.modalScrollPosition : this.pageScrollPosition;
      this.pageScrollPosition = restoreScrollY;
      this.updateModalViewportLock(restoreScrollY);
      this.clearActivityQuery(restoreScrollY);
      this.persistModalSession();
      this.scheduleIdleModalCleanup();
      return;
    }

    this.syncCurrentActivityModeUrl(false);
    this.persistModalSession();
  }

  hasActivityThumbnailUrl(activity: EditGroupActivity): boolean {
    return !!activity.thumbnailUrl?.trim();
  }

  onListThumbError(activity: EditGroupActivity) {
    activity.thumbnailUrl = '';
    this.cdr.markForCheck();
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
    this.persistPageState();
  }

  private buildStatusOptions() {
    this.statusOptions = [
      { label: this.translate.instant('activitiesManagement.allStatuses'), value: 'all' },
      { label: this.translate.instant('activityStatus.incoming'), value: 'incoming' },
      { label: this.translate.instant('activityStatus.completed'), value: 'completed' },
      { label: this.translate.instant('activityStatus.canceled'), value: 'canceled' }
    ];
  }

  get quickUpcoming(): boolean {
    return this.selectedStatus === 'incoming';
  }

  toggleQuickUpcoming() {
    this.selectedStatus = this.quickUpcoming ? 'all' : 'incoming';
    this.onFilterChanged();
  }

  toggleQuickThisWeek() {
    this.quickThisWeek = !this.quickThisWeek;
    if (this.quickThisWeek) {
      // Monday to Sunday of the running week, in local time like the calendar.
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      this.dateRangeSelection = [monday, sunday];
      this.selectedStartDate = toIsoDate(monday);
      this.selectedEndDate = toIsoDate(sunday);
    } else {
      this.dateRangeSelection = undefined;
      this.selectedStartDate = '';
      this.selectedEndDate = '';
    }
    this.onFilterChanged();
  }

  toggleQuickWithoutPartners() {
    this.quickWithoutPartners = !this.quickWithoutPartners;
    this.onFilterChanged();
  }

  toggleQuickFull() {
    this.quickFull = !this.quickFull;
    this.onFilterChanged();
  }

  toggleQuickWeekly() {
    this.quickWeekly = !this.quickWeekly;
    this.onFilterChanged();
  }

  /** The alert KPI drills straight into the rows it counts. */
  focusWithoutPartners() {
    this.quickWithoutPartners = true;
    this.selectedStatus = 'incoming';
    this.onFilterChanged();
    this.document.querySelector('.management-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Health tone of a growth figure: green up, red down, orange in between. */
  growthClass(value: number | null, goodWhenDown = false): string {
    if (value === null) {
      return 'kpi-growth--mid';
    }
    return `kpi-growth--${growthTone(value, { goodWhenDown })}`;
  }

  /** Partner seats the activity offers, the organiser's own seat removed. */
  partnerCapacity(activity: EditGroupActivity): number | null {
    return activity.maxAttendees != null ? Math.max(activity.maxAttendees - 1, 0) : null;
  }

  fillPercent(activity: EditGroupActivity): number {
    const capacity = this.partnerCapacity(activity);
    return capacity ? Math.min((activity.participants / capacity) * 100, 100) : 0;
  }

  onFilterChanged() {
    this.searchReload.clear();
    this.tableFirst = 0;
    this.persistPageState();
    this.loadActivities(false);
  }

  onSearchChanged() {
    this.tableFirst = 0;
    this.searchReload.schedule(() => {
      this.persistPageState();
      this.loadActivities(false);
    });
  }

  clearFilter() {
    this.quickWithoutPartners = false;
    this.quickFull = false;
    this.quickWeekly = false;
    this.quickThisWeek = false;
    this.searchQuery = '';
    this.selectedStatus = 'all';
    this.selectedSportId = '';
    this.locationQuery = '';
    this.selectedStartDate = '';
    this.selectedEndDate = '';
    this.dateRangeSelection = undefined;
    this.searchReload.clear();
    this.tableFirst = 0;
    this.persistPageState();
    this.loadActivities(false);
  }

  openDeleteActivityMode(activity: EditGroupActivity) {
    const isIncoming = this.isActivityIncoming(activity);
    const isFree = activity.type === 'free' || !activity.type;
    const hasNoParticipants = activity.participants === 0;
    const isPaid = activity.type === 'paid';
    const hasParticipants = activity.participants > 0;

    if (isIncoming && isPaid && hasParticipants) {
      this.openCancelPaidDialog(activity);
      return;
    }

    if (isIncoming && (isFree || hasNoParticipants)) {
      this.openCancelDialog(activity);
      return;
    }

    const scrollY = this.document.defaultView?.scrollY ?? this.pageScrollPosition;
    this.pageScrollPosition = scrollY;
    this.persistPageState();
    this.activityDrawerHost.markInitialized();
    this.activityDrawerHost.openDeleteMode(activity);
    this.updateModalViewportLock(scrollY);
    this.persistModalSession();
    this.syncActivityModeUrl(activity.id, scrollY, 'delete');
  }

  openCancelDialog(activity: EditGroupActivity) {
    this.activityToCancel = activity;
    this.cancelDialogClosing = false;
    this.skipCancelDialogEnterAnimation = false;
    this.cancelConfirmed = false;
    this.cancelDialogVisible = true;
  }

  closeCancelDialog() {
    this.skipCancelDialogEnterAnimation = false;
    this.cancelDialogVisible = false;
    this.cancelDialogClosing = true;
  }

  onCancelDialogHidden() {
    if (this.cancelDialogVisible) {
      return;
    }

    this.cancelDialogClosing = false;
    this.skipCancelDialogEnterAnimation = false;
    this.cancelConfirmed = false;
    this.activityToCancel = null;
  }

  onCancelConfirmedChange(value: boolean) {
    this.cancelConfirmed = value;
  }

  onCancelConfirmed() {
    if (!this.activityToCancel) {
      return;
    }

    const activityId = this.activityToCancel.id;
    const activityName = this.activityToCancel.name || 'this activity';
    this.closeCancelDialog();

    this.subscriptions.add(this.activitiesApi.cancelActivity(activityId).subscribe({
      next: () => {
        this.deleteSuccessMessage = `Activity "${activityName}" has been canceled`;
        this.scheduleToastDismiss(() => {
          this.deleteSuccessMessage = '';
        });
        this.loadActivities(true, true);
        this.loadSummaryData();
        this.cdr.markForCheck();
      },
      error: error => {
        this.operationErrorMessage = this.getApiErrorMessage(error, 'Unable to cancel activity. Please try again.');
        this.cdr.markForCheck();
      }
    }));
  }

  openCancelPaidDialog(activity: EditGroupActivity) {
    this.activityToCancel = activity;
    this.cancelPaidDialogClosing = false;
    this.skipCancelPaidDialogEnterAnimation = false;
    this.cancelPaidConfirmed = false;
    this.cancelPaidDialogVisible = true;
  }

  closeCancelPaidDialog() {
    this.skipCancelPaidDialogEnterAnimation = false;
    this.cancelPaidDialogVisible = false;
    this.cancelPaidDialogClosing = true;
  }

  onCancelPaidDialogHidden() {
    if (this.cancelPaidDialogVisible) {
      return;
    }

    this.cancelPaidDialogClosing = false;
    this.skipCancelPaidDialogEnterAnimation = false;
    this.cancelPaidConfirmed = false;
    this.activityToCancel = null;
  }

  onCancelPaidConfirmedChange(value: boolean) {
    this.cancelPaidConfirmed = value;
  }

  onCancelPaidConfirmed() {
    if (!this.activityToCancel) {
      return;
    }

    const activityId = this.activityToCancel.id;
    const activityName = this.activityToCancel.name || 'this activity';
    this.closeCancelPaidDialog();

    this.subscriptions.add(this.activitiesApi.cancelActivity(activityId).subscribe({
      next: () => {
        this.deleteSuccessMessage = `Activity "${activityName}" has been canceled`;
        this.scheduleToastDismiss(() => {
          this.deleteSuccessMessage = '';
        });
        this.loadActivities(true, true);
        this.loadSummaryData();
        this.cdr.markForCheck();
      },
      error: error => {
        this.operationErrorMessage = this.getApiErrorMessage(error, 'Unable to cancel activity. Please try again.');
        this.cdr.markForCheck();
      }
    }));
  }

  getActivityGroup(activity: EditGroupActivity): string {
    const name = activity.groupName?.trim();
    return name ? name : '--';
  }

  getActivityGroupSort(activity: EditGroupActivity): string {
    return this.getActivityGroup(activity).toLowerCase();
  }

  getActivityStatus(activity: EditGroupActivity): ActivityStatus {
    return (activity.status as ActivityStatus | undefined) ?? 'incoming';
  }

  getActivityStatusLabel(activity: EditGroupActivity): string {
    switch (this.getActivityStatus(activity)) {
      case 'incoming':
        return 'Incoming';
      case 'completed':
        return 'Completed';
      case 'canceled':
        return 'Canceled';
    }
  }

  isActivityIncoming(activity: EditGroupActivity): boolean {
    return this.getActivityStatus(activity) === 'incoming';
  }

  getActivityActionTooltip(activity: EditGroupActivity): string {
    return this.isActivityIncoming(activity) ? 'Cancel activity' : 'Delete activity';
  }

  getActivityDate(activity: EditGroupActivity): Date {
    const date = new Date(activity.startDateIso ?? `${activity.date} ${activity.time}`);
    return Number.isNaN(date.getTime()) ? new Date(0) : date;
  }

  getActivityEndDate(activity: EditGroupActivity): Date {
    if (activity.endDateIso) {
      const apiEndDate = new Date(activity.endDateIso);
      if (!Number.isNaN(apiEndDate.getTime())) {
        return apiEndDate;
      }
    }

    const endDate = new Date(this.getActivityDate(activity));
    endDate.setMinutes(endDate.getMinutes() + 120);
    return endDate;
  }

  isWeeklyActivity(activity: EditGroupActivity): boolean {
    return activity.recurrence === 'weekly';
  }

  getActivityRangeEndDate(activity: EditGroupActivity): Date {
    if (activity.endDateIso) {
      const apiEndDate = new Date(activity.endDateIso);
      if (!Number.isNaN(apiEndDate.getTime())) {
        return apiEndDate;
      }
    }

    const endDate = new Date(this.getActivityDate(activity));
    endDate.setDate(endDate.getDate() + 30);
    return endDate;
  }

  getSportIconPath(sport: string): string {
    return getSportIconPath(sport || 'allsports', this.sportTypeConfigs);
  }

  getSportOptionValue(sport: string): string {
    return getSportOptionValue(sport);
  }

  toggleDateRangeCalendar(event?: MouseEvent) {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.dateRangeCalendar?.overlayVisible) {
      this.dateRangeCalendar.hideOverlay();
      return;
    }

    this.syncDateRangeCalendarPanelWidth();
    this.dateRangeCalendar?.showOverlay();
  }

  onDateRangeCalendarShow() {
    requestAnimationFrame(() => this.alignDateRangeCalendarOverlay());
  }

  onDateRangeCalendarNavigate() {
    requestAnimationFrame(() => this.alignDateRangeCalendarOverlay());
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChanged() {
    if (this.dateRangeCalendar?.overlayVisible) {
      this.alignDateRangeCalendarOverlay();
    }

    if (!this.initialPageScrollRestored || this.isAnyModalVisible()) {
      return;
    }

    this.pageScrollPosition = this.document.defaultView?.scrollY ?? 0;
    this.persistPageState();
  }

  @HostListener('window:beforeunload')
  @HostListener('window:pagehide')
  onBeforePageUnload() {
    const scrollY = this.isAnyModalVisible()
      ? this.modalScrollPosition
      : this.postRestoreScrollGuardLockActive
        ? this.postRestoreTargetScrollY
        : this.document.defaultView?.scrollY ?? this.pageScrollPosition;
    this.pageScrollPosition = scrollY;
    this.persistPageState();
    this.persistModalSession();
  }

  onDateRangeSelected() {
    const [startDate, endDate] = this.dateRangeSelection ?? [];
    this.selectedStartDate = startDate ? toIsoDate(startDate) : '';
    this.selectedEndDate = endDate ? toIsoDate(endDate) : this.selectedStartDate;
    this.tableFirst = 0;
    this.persistPageState();
    this.loadActivities(false);

  }

  getDateRangeStartLabel(): string {
    const [startDate] = this.dateRangeSelection ?? [];
    return startDate ? formatDisplayDate(startDate) : 'Start date';
  }

  getDateRangeEndLabel(): string {
    const endDate = this.dateRangeSelection?.[1];
    return endDate ? formatDisplayDate(endDate) : 'End date';
  }

  hasDateRangeStart(): boolean {
    return this.dateRangeSelection?.[0] instanceof Date;
  }

  hasDateRangeEnd(): boolean {
    return this.dateRangeSelection?.[1] instanceof Date;
  }

  onTableLazyLoad(event: TableLazyLoadEvent) {
    if (this.shouldIgnoreInitialLazyLoad) {
      this.shouldIgnoreInitialLazyLoad = false;
      return;
    }

    const tableState = getManagementTableLazyState(event, this.tableRows, this.isActivitiesListSortField);
    this.tableFirst = tableState.first;
    this.tableRows = tableState.rows;
    const sortState = this.getNextSortState(tableState.sortField, tableState.sortOrder);
    this.tableSortField = sortState.sortField;
    this.tableSortOrder = sortState.sortOrder;
    if (sortState.cleared) {
      this.clearTableSort(this.activitiesTable);
    }
    this.persistPageState();
    this.loadActivities();
  }

  private getNextSortState(
    sortField?: ActivitiesListSortField,
    sortOrder?: 'asc' | 'desc'
  ): { sortField?: ActivitiesListSortField; sortOrder?: 'asc' | 'desc'; cleared?: boolean } {
    const shouldClearSort =
      sortField === this.tableSortField &&
      this.tableSortOrder === 'desc' &&
      sortOrder === 'asc';

    if (shouldClearSort) {
      return { cleared: true };
    }

    return {
      sortField,
      sortOrder
    };
  }

  private clearTableSort(table?: Table) {
    if (!table) {
      return;
    }

    table.sortField = null;
    table.sortOrder = 0;
    table.tableService.onSort(null);
  }

  private loadSummaryData() {
    this.summaryLoading = true;
    this.subscriptions.add(this.activitiesApi.getSummary().subscribe({
      next: summary => {
        this.activitySummary = summary;
        this.summaryLoading = false;
        this.cdr.markForCheck();
      },
      error: error => {
        this.summaryLoading = false;
        this.showOperationError(this.getApiErrorMessage(
          error,
          'Unable to load activity KPI cards. Please try again.'
        ));
        this.cdr.markForCheck();
      }
    }));
  }

  private loadReferenceData() {
    this.loadSummaryData();

    this.subscriptions.add(this.activitiesApi.getSportTypes().subscribe({
      next: sportTypes => {
        this.sportTypeConfigs = sportTypes;
        this.editGroupSportOptions = toTwoColumnOptions(
          sportTypes.map(({ label, value }) => ({ label, value }))
        );
        this.cdr.markForCheck();
      },
      error: error => {
        this.showOperationError(this.getApiErrorMessage(
          error,
          'Unable to load sport types. Please try again.'
        ));
        this.cdr.markForCheck();
      }
    }));
  }

  private loadActivities(showLoadingIndicator = true, forceReload = false) {
    const query = this.getActivitiesListQuery();
    const queryKey = this.getActivitiesQueryKey(query);
    if (!forceReload && (this.inFlightActivitiesQueryKey === queryKey || this.loadedActivitiesQueryKey === queryKey)) {
      return;
    }

    this.activitiesRequestSubscription?.unsubscribe();
    this.inFlightActivitiesQueryKey = queryKey;
    if (showLoadingIndicator) {
      this.tableLoading = true;
    }

    this.activitiesRequestSubscription = this.activitiesApi.list(query).subscribe({
      next: response => {
        if (!response.data.length && response.total > 0 && this.tableFirst >= response.total) {
          this.tableFirst = getPreviousPageFirst(response.total, this.tableRows);
          this.inFlightActivitiesQueryKey = '';
          this.loadActivities(true, true);
          return;
        }

        this.loadedActivitiesQueryKey = queryKey;
        this.inFlightActivitiesQueryKey = '';
        this.activities = response.data;
        this.totalRecords = response.total;
        this.tableRows = response.pageSize || this.tableRows;
        this.tableFirst = Math.max((response.page || 1) - 1, 0) * this.tableRows;
        this.tableLoading = false;
        this.persistPageState();
        this.restorePageScrollPosition();
        this.cdr.markForCheck();
      },
      error: error => {
        this.inFlightActivitiesQueryKey = '';
        this.activities = [];
        this.totalRecords = 0;
        this.tableLoading = false;
        this.showOperationError(this.getApiErrorMessage(
          error,
          'Unable to load activities. Please try again.'
        ));
        this.restorePageScrollPosition();
        this.cdr.markForCheck();
      }
    });
  }

  private showOperationError(message: string) {
    this.operationErrorMessage = message;
    this.scheduleToastDismiss(() => {
      this.operationErrorMessage = '';
    });
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    const apiError = this.normalizeApiError(error);
    const rawMessage = apiError.messages.find(message => message.trim());
    if (rawMessage) {
      return rawMessage;
    }

    switch (apiError.status) {
      case 0:
        return 'Cannot connect to the server right now. Please check your connection and try again.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'This activity may have been deleted or is no longer available.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
      case 502:
      case 503:
      case 504:
        return 'The server is temporarily unavailable. Please try again later.';
      default:
        return fallback;
    }
  }

  private normalizeApiError(error: unknown): { status?: number; messages: string[] } {
    const messages: string[] = [];
    const visit = (value: unknown, depth = 0): number | undefined => {
      if (!value || depth > 4) {
        return undefined;
      }
      if (typeof value === 'string') {
        messages.push(value);
        return undefined;
      }
      if (Array.isArray(value)) {
        value.forEach(item => visit(item, depth + 1));
        return undefined;
      }
      if (typeof value !== 'object') {
        return undefined;
      }

      const maybeError = value as {
        status?: unknown;
        statusCode?: unknown;
        error?: unknown;
        message?: unknown;
        detail?: unknown;
        details?: unknown;
        title?: unknown;
      };
      const status = typeof maybeError.status === 'number'
        ? maybeError.status
        : typeof maybeError.statusCode === 'number'
          ? maybeError.statusCode
          : undefined;

      [maybeError.message, maybeError.detail, maybeError.details, maybeError.title]
        .forEach(item => visit(item, depth + 1));
      if (maybeError.error && maybeError.error !== value) {
        return status ?? visit(maybeError.error, depth + 1);
      }
      return status;
    };

    return { status: visit(error), messages };
  }

  private getActivitiesQueryKey(query: ActivitiesListQuery): string {
    return JSON.stringify(query);
  }

  exportingCsv = false;

  /**
   * Downloads the filtered list. The file is built by the API, so what is
   * exported always matches what the filters describe rather than the rows
   * that happen to be on screen.
   */
  exportCsv(): void {
    if (this.exportingCsv) {
      return;
    }
    this.exportingCsv = true;

    this.activitiesApi.exportCsv(this.getActivitiesListQuery()).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `activities-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        // Released straight away; the browser has already taken the data.
        URL.revokeObjectURL(url);
        this.exportingCsv = false;
      },
      error: () => {
        this.exportingCsv = false;
      },
    });
  }

  private getActivitiesListQuery(): ActivitiesListQuery {
    return {
      search: this.searchQuery,
      status: this.selectedStatus as ActivityStatus | 'all',
      sportId: this.selectedSportId || undefined,
      location: this.locationQuery.trim() || undefined,
      startDate: this.selectedStartDate,
      endDate: this.selectedEndDate,
      sortField: this.tableSortField,
      sortOrder: this.tableSortOrder,
      withoutPartners: this.quickWithoutPartners || undefined,
      recurring: this.quickWeekly || undefined,
      full: this.quickFull || undefined,
      page: Math.floor(this.tableFirst / this.tableRows) + 1,
      pageSize: this.tableRows
    };
  }

  private isActivitiesListSortField(field?: string | string[] | null): field is ActivitiesListSortField {
    return field === 'name' ||
      field === 'group' ||
      field === 'date' ||
      field === 'status' ||
      field === 'participants';
  }

  private clearToastTimeout() {
    this.toastDismiss.clear();
  }

  private scheduleToastDismiss(dismiss: () => void) {
    this.toastDismiss.schedule(dismiss, () => this.cdr.markForCheck());
  }

  private syncDateRangeCalendarPanelWidth() {
    const width = this.dateRangeField?.nativeElement.getBoundingClientRect().width ?? 348;
    this.dateRangeCalendarPanelStyle = {
      width: `${Math.round(width)}px`,
      minWidth: `${Math.round(width)}px`
    };
    this.cdr.markForCheck();
  }

  private alignDateRangeCalendarOverlay() {
    const trigger = this.dateRangeField?.nativeElement;
    const overlay = document.querySelector<HTMLElement>('.activity-date-calendar-popover');
    if (!trigger || !overlay) {
      return;
    }

    const gap = 5;
    const triggerRect = trigger.getBoundingClientRect();
    const overlayHeight = overlay.offsetHeight;
    const overlayWidth = Math.round(triggerRect.width);
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openAbove = spaceBelow < overlayHeight + gap && spaceAbove > spaceBelow;
    const viewportTop = openAbove
      ? triggerRect.top - overlayHeight - gap
      : triggerRect.bottom + gap;

    overlay.classList.toggle('activity-date-calendar-popover--above', openAbove);
    overlay.style.left = `${Math.round(window.scrollX + triggerRect.left)}px`;
    overlay.style.top = `${Math.round(window.scrollY + viewportTop)}px`;
    overlay.style.width = `${overlayWidth}px`;
    overlay.style.minWidth = `${overlayWidth}px`;
  }

  private applyStoredPageState() {
    const state = this.pageSession.readPageState();
    if (!state) {
      return;
    }

    this.showFilter = state.showFilter;
    this.searchQuery = state.searchQuery ?? '';
    this.selectedStatus = state.selectedStatus ?? 'all';
    this.selectedStartDate = state.selectedStartDate ?? '';
    this.selectedEndDate = state.selectedEndDate ?? '';
    this.tableFirst = state.tableFirst ?? 0;
    this.tableRows = state.tableRows ?? 10;
    this.tableSortField = state.tableSortField;
    this.tableSortOrder = state.tableSortOrder;
    this.quickWithoutPartners = state.quickWithoutPartners ?? false;
    this.quickFull = state.quickFull ?? false;
    this.quickWeekly = state.quickWeekly ?? false;
    this.dateRangeSelection = this.getStoredDateRangeSelection();
    this.pageScrollPosition = state.pageScrollPosition ?? 0;
    this.activities = state.activitiesSnapshot ?? [];
    this.totalRecords = state.totalRecords ?? this.activities.length;

    if (this.isReloadNavigation) {
      this.pageScrollPosition = 0;
    }
  }

  private getStoredDateRangeSelection(): Date[] | undefined {
    const startDate = fromIsoDate(this.selectedStartDate);
    const endDate = fromIsoDate(this.selectedEndDate);
    if (!startDate) {
      return undefined;
    }

    return endDate ? [startDate, endDate] : [startDate];
  }

  private prepareEarlyPageScrollLock() {
    const scrollY = this.getInitialScrollRestorePosition();
    this.pendingPageScrollRestore = scrollY;
    this.lockPageScrollVisual(scrollY);
  }

  private getInitialScrollRestorePosition(): number {
    const queryParamMap = this.route.snapshot.queryParamMap;
    const hasModeQuery =
      queryParamMap.has('activity') ||
      queryParamMap.has('delete') ||
      queryParamMap.has('mode');

    if (hasModeQuery) {
      const storedState = this.pageSession.readModalState();
      if (storedState?.url === this.location.path() && this.hasVisibleModalInSession(storedState)) {
        if (this.isReloadNavigation) {
          const stateWithoutDeleteDialog = {
            ...storedState,
            deleteDialogVisible: false,
            deleteDialogClosing: false
          };
          if (!this.hasVisibleModalInSession(stateWithoutDeleteDialog)) {
            return 0;
          }
        }
        return storedState.modalScrollPosition ?? 0;
      }
    }

    return this.pageScrollPosition;
  }

  private lockPageScrollVisual(scrollY: number) {
    this.viewportScroll.lockPageAt(scrollY);
    this.hasEarlyPageScrollLock = true;
  }

  private releasePendingPageScrollLock() {
    if (!this.hasEarlyPageScrollLock) {
      return;
    }

    this.viewportScroll.releasePageLock();
    this.hasEarlyPageScrollLock = false;
  }

  private persistPageState() {
    this.pageSession.savePageState({
      showFilter: this.showFilter,
      searchQuery: this.searchQuery,
      selectedStatus: this.selectedStatus as ActivityStatus | 'all',
      selectedStartDate: this.selectedStartDate,
      selectedEndDate: this.selectedEndDate,
      tableFirst: this.tableFirst,
      tableRows: this.tableRows,
      tableSortField: this.tableSortField,
      tableSortOrder: this.tableSortOrder,
      quickWithoutPartners: this.quickWithoutPartners,
      quickFull: this.quickFull,
      quickWeekly: this.quickWeekly,
      pageScrollPosition: this.pageScrollPosition,
      activitiesSnapshot: this.activities,
      totalRecords: this.totalRecords
    });
  }

  private isAnyModalVisible(): boolean {
    return !!this.activityDrawerHost?.visible ||
      !!this.activityDrawerHost?.deleteDialogVisible ||
      !!this.activityDrawerHost?.deleteDialogClosing;
  }

  private hasVisibleModalInSession(state: { activityDrawer: { visible: boolean }; deleteDialogVisible: boolean; deleteDialogClosing?: boolean }): boolean {
    return !!state.activityDrawer.visible || !!state.deleteDialogVisible || !!state.deleteDialogClosing;
  }

  private updateModalViewportLock(restoredScrollPosition?: number) {
    if (this.hasEarlyPageScrollLock) {
      this.pendingPageScrollRestore = 0;
      this.releasePendingPageScrollLock();
    }

    if (!this.isAnyModalVisible()) {
      this.unlockModalViewport();
      return;
    }

    const pendingRestoreScrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : null;
    const scrollY = restoredScrollPosition ??
      (this.modalViewportLocked ? this.modalScrollPosition : null) ??
      pendingRestoreScrollY ??
      this.document.defaultView?.scrollY ??
      0;
    this.modalScrollPosition = scrollY;
    this.pageScrollPosition = scrollY;
    this.persistPageState();

    if (this.modalViewportLocked) {
      this.viewportScroll.updateModalBackgroundLock(this.modalScrollPosition);
      return;
    }

    this.viewportScroll.lockModalBackground(
      this.modalScrollPosition,
      this.preventBackgroundPointerScroll,
      this.preventBackgroundKeyboardScroll
    );
    this.modalViewportLocked = true;
  }

  private unlockModalViewport() {
    if (!this.modalViewportLocked) {
      return;
    }

    this.viewportScroll.unlockModalBackground(
      this.modalScrollPosition,
      this.preventBackgroundPointerScroll,
      this.preventBackgroundKeyboardScroll
    );
    this.modalViewportLocked = false;
  }

  private scheduleIdleModalCleanup() {
    this.document.defaultView?.requestAnimationFrame(() => {
      this.cleanupIdleModalArtifacts();
      this.document.defaultView?.setTimeout(() => this.cleanupIdleModalArtifacts(), 180);
    });
  }

  private cleanupIdleModalArtifacts() {
    if (this.isAnyModalVisible()) {
      return;
    }

    this.unlockModalViewport();
    this.document.body.classList.remove('groups-modal-background-locked');
    this.document.body.style.removeProperty('--groups-modal-scroll-top');
    this.document.removeEventListener('wheel', this.preventBackgroundPointerScroll, true);
    this.document.removeEventListener('touchmove', this.preventBackgroundPointerScroll, true);
    this.document.removeEventListener('keydown', this.preventBackgroundKeyboardScroll, true);

    if (this.document.querySelector('.p-sidebar-active')) {
      return;
    }

    this.document.querySelectorAll<HTMLElement>('body > .p-sidebar-mask').forEach(mask => {
      mask.remove();
    });
  }

  private isDeleteDialogBlocked(): boolean {
    return !!this.activityDrawerHost?.deleteDialogVisible ||
      !!this.activityDrawerHost?.deleteDialogClosing;
  }

  private isTopDrawerBodyTarget(target: EventTarget | null): boolean {
    return this.viewportScroll.isTopDrawerBodyTarget(
      target,
      this.isDeleteDialogBlocked()
    );
  }

  private restorePageScrollPosition() {
    if (this.initialPageScrollRestored || this.isAnyModalVisible()) {
      return;
    }

    const desiredScrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : this.pageScrollPosition;
    if (desiredScrollY <= 0) {
      this.initialPageScrollRestored = true;
      this.releasePendingPageScrollLock();
      this.document.defaultView?.scrollTo(0, 0);
      return;
    }

    this.restorePageScrollPositionWithRetry(desiredScrollY, 0);
  }

  private restorePageScrollPositionWithRetry(scrollY: number, attempt: number) {
    this.document.defaultView?.requestAnimationFrame(() => {
      if (this.isAnyModalVisible()) {
        return;
      }

      this.document.defaultView?.scrollTo(0, scrollY);
      const currentScroll = this.document.defaultView?.scrollY ?? 0;
      if (Math.abs(currentScroll - scrollY) <= 1 || attempt >= 12) {
        this.pendingPageScrollRestore = 0;
        this.pageScrollPosition = scrollY;
        this.initialPageScrollRestored = true;
        this.startPostRestoreScrollGuard(scrollY);
        return;
      }

      this.restorePageScrollPositionWithRetry(scrollY, attempt + 1);
    });
  }

  private startPostRestoreScrollGuard(scrollY: number) {
    if (scrollY <= 0) {
      this.releasePendingPageScrollLock();
      return;
    }

    this.stopPostRestoreScrollGuard();
    this.postRestoreTargetScrollY = scrollY;
    this.lockPageScrollVisual(scrollY);
    this.postRestoreScrollGuardLockActive = true;
    this.postRestoreScrollGuardUntil = Date.now() + 2500;

    const tick = () => {
      if (Date.now() > this.postRestoreScrollGuardUntil || this.isAnyModalVisible()) {
        this.stopPostRestoreScrollGuard();
        return;
      }

      const currentScrollY = this.document.defaultView?.scrollY ?? 0;
      if (currentScrollY <= 1) {
        this.document.defaultView?.scrollTo(0, scrollY);
      }

      this.postRestoreScrollGuardFrame = this.document.defaultView?.requestAnimationFrame(tick) ?? null;
    };

    this.postRestoreScrollGuardFrame = this.document.defaultView?.requestAnimationFrame(tick) ?? null;
  }

  private stopPostRestoreScrollGuard() {
    if (this.postRestoreScrollGuardFrame === null) {
      if (this.postRestoreScrollGuardLockActive) {
        this.releasePendingPageScrollLock();
        this.postRestoreScrollGuardLockActive = false;
      }
      return;
    }

    this.document.defaultView?.cancelAnimationFrame(this.postRestoreScrollGuardFrame);
    this.postRestoreScrollGuardFrame = null;
    this.postRestoreScrollGuardUntil = 0;
    this.postRestoreTargetScrollY = 0;
    if (this.postRestoreScrollGuardLockActive) {
      this.releasePendingPageScrollLock();
      this.postRestoreScrollGuardLockActive = false;
    }
  }

  private beginScrollRestoration() {
    this.scrollRestorationPending = true;
    this.document.documentElement.classList.add('groups-scroll-restoring');
  }

  private completeScrollRestoration() {
    if (!this.scrollRestorationPending) {
      return;
    }

    this.scrollRestorationPending = false;
    this.document.documentElement.classList.remove('groups-scroll-restoring');
  }

  private restoreActivityModalSession(activity: EditGroupActivity): boolean {
    const storedState = this.pageSession.readModalState();
    if (!storedState || storedState.url !== this.location.path() || storedState.activityDrawer.draft?.id !== activity.id) {
      return false;
    }

    this.applyStoredActivityModalSession(storedState, activity.id);
    return true;
  }

  private restoreActivityModalSessionFromStorage(activityId: string): boolean {
    const storedState = this.pageSession.readModalState();
    if (!storedState || storedState.url !== this.location.path() || storedState.activityDrawer.draft?.id !== activityId) {
      return false;
    }

    this.applyStoredActivityModalSession(storedState, activityId);
    return true;
  }

  private applyStoredActivityModalSession(storedState: NonNullable<ReturnType<ActivitiesManagementPageSessionService['readModalState']>>, activityId: string) {
    this.modalScrollPosition = storedState.modalScrollPosition ?? this.pageScrollPosition;
    this.pageScrollPosition = this.modalScrollPosition;
    this.activityDrawerHost.markInitialized();
    if (this.isReloadNavigation && storedState.activityDrawer.visible) {
      this.beginScrollRestoration();
    }
    this.restoringModalSession = true;
    this.activityDrawerHost.applySessionState({
      ...storedState.activityDrawer,
      skipEnterAnimation: true,
      visible: storedState.activityDrawer.visible
    });
    this.activityDrawerHost.deleteConfirmed = this.isReloadNavigation ? false : storedState.deleteConfirmed;
    this.activityDrawerHost.deleteDialogClosing = false;
    this.activityDrawerHost.deleteDialogVisible = this.isReloadNavigation ? false : storedState.deleteDialogVisible;
    this.lastActivityDeleteDialogActive = this.activityDrawerHost.deleteDialogVisible || this.activityDrawerHost.deleteDialogClosing;
    this.restoringModalSession = false;
    this.initialPageScrollRestored = true;
    this.updateModalViewportLock(this.modalScrollPosition);
    if (storedState.activityDrawer.visible) {
      this.restoreActivityDrawerScrollPosition(storedState.activityDrawer.scrollPosition ?? 0, true);
    }
    if (this.isReloadNavigation && storedState.deleteDialogVisible) {
      if (storedState.activityDrawer.visible) {
        this.syncActivityModeUrl(activityId, this.modalScrollPosition, 'view');
      } else {
        this.pageSession.clearModalState();
        this.clearActivityQuery(this.modalScrollPosition);
      }
    }
  }

  private shouldClearReloadedStandaloneDelete(): boolean {
    if (!this.isReloadNavigation || this.route.snapshot.queryParamMap.get('delete') !== 'activity') {
      return false;
    }

    const storedState = this.pageSession.readModalState();
    return !storedState || storedState.url !== this.location.path() || !storedState.activityDrawer.visible;
  }

  private restoreActivityFromUrl(activity: EditGroupActivity) {
    const params = this.route.snapshot.queryParamMap;
    const mode = params.get('mode');
    const deleteMode = params.get('delete') === 'activity' && !this.isReloadNavigation;

    if (deleteMode) {
      const scrollY = this.pendingPageScrollRestore > 0 ? this.pendingPageScrollRestore : this.pageScrollPosition;
      this.pageScrollPosition = scrollY;
      this.activityDrawerHost.markInitialized();
      this.activityDrawerHost.openDeleteMode(activity);
      this.updateModalViewportLock(scrollY);
      this.syncActivityModeUrl(activity.id, scrollY, 'delete');
      return;
    }

    this.openActivityFromList(activity, false, {
      editMode: mode === 'edit',
      keepUndoInEditMode: false,
      skipEnterAnimation: this.isReloadNavigation || mode === 'edit'
    });
    if (this.isReloadNavigation) {
      this.beginScrollRestoration();
      this.restoreActivityDrawerScrollPosition(0, true);
    }
    this.syncActivityModeUrl(activity.id, this.pageScrollPosition, mode === 'edit' ? 'edit' : 'view');
  }

  private restoreActivityDrawerScrollPosition(scrollPosition: number, revealAfterRestore = false, attempt = 0) {
    if (scrollPosition <= 0 && !revealAfterRestore) {
      return;
    }

    this.document.defaultView?.requestAnimationFrame(() => {
      const body = this.document.querySelector<HTMLElement>('.activity-detail-sidebar .edit-drawer-body');
      if (!body) {
        if (attempt < 12) {
          this.restoreActivityDrawerScrollPosition(scrollPosition, revealAfterRestore, attempt + 1);
          return;
        }
        if (revealAfterRestore) {
          this.completeScrollRestoration();
        }
        return;
      }

      body.scrollTop = scrollPosition;
      if (Math.abs(body.scrollTop - scrollPosition) <= 1 || attempt >= 12) {
        if (revealAfterRestore) {
          this.completeScrollRestoration();
        }
        return;
      }

      this.restoreActivityDrawerScrollPosition(scrollPosition, revealAfterRestore, attempt + 1);
    });
  }

  private persistModalSession() {
    if (!this.activityDrawerHost || (!this.isAnyModalVisible() && !this.activityDrawerHost.activityDraft)) {
      this.pageSession.clearModalState();
      return;
    }

    const activityDrawer = this.activityDrawerHost.getSessionState();
    if (!activityDrawer.visible && !this.activityDrawerHost.deleteDialogVisible && !this.activityDrawerHost.deleteDialogClosing) {
      this.pageSession.clearModalState();
      return;
    }
    if (!activityDrawer.draft) {
      this.pageSession.clearModalState();
      return;
    }

    this.pageSession.saveModalState({
      url: this.location.path(),
      modalScrollPosition: this.modalScrollPosition,
      activityDrawer,
      deleteDialogVisible: this.activityDrawerHost.deleteDialogVisible,
      deleteDialogClosing: this.activityDrawerHost.deleteDialogClosing,
      deleteConfirmed: this.activityDrawerHost.deleteConfirmed
    });
  }

  private setActivityQuery(activityId: string, restoreScrollY = this.pageScrollPosition) {
    this.syncActivityModeUrl(activityId, restoreScrollY, 'view');
  }

  private syncCurrentActivityModeUrl(restoreAfterUrlSync = true) {
    const activityId = this.activityDrawerHost?.activityDraft?.id;
    if (!activityId || !this.isAnyModalVisible()) {
      return;
    }

    const mode = this.activityDrawerHost.deleteDialogVisible || this.activityDrawerHost.deleteDialogClosing
      ? 'delete'
      : this.activityDrawerHost.isEditActionMode()
        ? 'edit'
        : 'view';
    this.syncActivityModeUrl(activityId, this.modalScrollPosition, mode, restoreAfterUrlSync);
  }

  private syncActivityModeUrl(
    activityId: string,
    restoreScrollY = this.pageScrollPosition,
    mode: 'view' | 'edit' | 'delete' | undefined = 'view',
    restoreAfterUrlSync = true
  ) {
    const queryParams: Record<string, string | null> = {
      activity: activityId,
      mode: mode === 'edit' ? 'edit' : null,
      delete: mode === 'delete' ? 'activity' : null
    };
    const url = this.router.serializeUrl(this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge'
    }));
    this.location.replaceState(url);
    if (restoreAfterUrlSync) {
      this.restorePageScrollAfterUrlSync(restoreScrollY);
    }
    this.persistModalSession();
  }

  private clearActivityQuery(restoreScrollY = this.pageScrollPosition) {
    const url = this.router.serializeUrl(this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { activity: null, mode: null, delete: null },
      queryParamsHandling: 'merge'
    }));
    this.location.replaceState(url);
    this.restorePageScrollAfterUrlSync(restoreScrollY);
  }

  private restorePageScrollAfterUrlSync(scrollY: number) {
    const restore = () => {
      this.document.defaultView?.scrollTo(0, scrollY);
      this.pageScrollPosition = scrollY;
      this.persistPageState();
    };

    this.document.defaultView?.requestAnimationFrame(() => {
      restore();
      this.document.defaultView?.requestAnimationFrame(restore);
    });
  }
}
